/**
 * How the Kelly surface is hosted: the full-page route (`page`) or the right-side dock (`dock`). The mount site
 * is the only thing that knows, so this is context rather than store state and is never derived from a width
 * — size-driven behaviour belongs in the chat area's container queries, not here.
 */

import { createContext, useContext } from "react"
import type { DockView } from "../store/agent-chat-slice"

export type ChatLayoutMode = "page" | "dock"

/** A union, not optional callbacks: only a dock host has a Close and a Promote, and it always has both. */
export type ChatLayoutValue =
  | { mode: "page" }
  | {
    mode: "dock"
    /**
     * Which view the host is in. Carried because the surface's geometry changes with it and nothing remounts,
     * so the transcript needs a key to anchor its scroll against — NOT a size class, which stays a
     * container query on the chat area.
     */
    view: DockView
    /** The host closes the dock. */
    onRequestClose: () => void
    /** The host flips the dock between `docked` and `full`. */
    onToggleFull: () => void
    /**
     * The surface reports whether an artifact is open. A FACT, not a width: whether there is room to widen,
     * whether the user has since taken the width back, and what to hand back on close are all the host's.
     * Deriving anything about layout MODE from a width is prohibited.
     */
    onArtifactPresence: (hasArtifact: boolean) => void
  }

const PAGE_LAYOUT: ChatLayoutValue = { mode: "page" }

const ChatLayoutContext = createContext<ChatLayoutValue>(PAGE_LAYOUT)

export const ChatLayoutProvider = ChatLayoutContext.Provider

/** Deliberately does NOT throw outside the provider (unlike useKellyChat/useKellyPanel): `page` is the default. */
export function useChatLayout(): ChatLayoutValue {
  return useContext(ChatLayoutContext)
}

/**
 * Whether this layout seats the conversations list beside the transcript instead of over it.
 * Keyed off the VIEW, deliberately not off a width: the drawer was dropped while docked and
 * restored at stage width. A docked dock now runs from its 320px floor to `45vw`
 * (~1548px on a 3440 display), so the width no longer picks out the same docks the original arithmetic was
 * about — and the answer must not change under a drag. One predicate, so the chrome and the artifact panel's
 * collapse coordination cannot disagree about it.
 */
export function hasPermanentDrawer(layout: ChatLayoutValue): boolean {
  return layout.mode === "page" || layout.view === "full"
}
